import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { MetadataService } from '../../services/metadata.service';
import { UserService } from '../../services/user.service';
import { ProductService } from '../../services/product.service';
import { SearchService } from '../../search/services/search.service';
import { ViewerService } from '../../services/viewer.service';
import { CopyLinkComponent } from '../../components/copy-link/copy-link.component';
import dayjs from 'dayjs';

/**
 * Component for displaying the metadata of a given product in a modal.
 */
@Component({
	selector: 'metadata-quick-view',
	templateUrl: './metadata-quick-view.component.html',
	styleUrl: './metadata-quick-view.component.css'
})
export class MetadataQuickViewComponent implements OnInit {
	@Input() productId: any;

	LOCALIZATION: any;
	Object = Object;

	recordTitle: string = '';
	record: { [key: string]: any } = {};
	views: any;
	searchableMetadataFields: any[] = [];

	dataform: { tabs: any[]; iserror: boolean } = {
		tabs: [],
		iserror: false
	};

	activeTab = 0;
	view: string = 'standard';
	dateFormatDisplay: string = 'MM/DD/YY';
	pluralMapping = {
		'=1': '1 view ',
		'other': '# views '
	};

	constructor(
		private configService: ConfigService,
		private alertsService: AlertsService,
		private metadataService: MetadataService,
		private userService: UserService,
		private searchService: SearchService,
		private viewerService: ViewerService,
		private productService: ProductService,
		private ngbModal: NgbModal,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.view = this.configService.getMetadataTemplateType();
		this.searchableMetadataFields = this.configService.getConfig().search.searchableMetadataFields;

		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "USER_INTERFACE_DATE_TYPE" });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.loadRecord();
	}

	/**
	 * Loads the product metadata.
	 */
	loadRecord(): void {
		this.searchService.getRecord(this.productId).then(
			(response: any) => {
				let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "IS_HIGHLIGHT_VIEW_FIELD" });
				if (property.value === "yes") {
					this.viewerService.getHighlightedMetadataRecord(this.productId).then(
						(highlights: any) => {
							this.processMetadata(response, highlights);
						},
						(err: any) => {
							console.error("Failed to load product metadata", err);
							this.alertsService.error({
								message: "Failed to retrieve metadata for this document."
							});
						}
					);
				} else {
					this.processMetadata(response);
				}

				this.loadDocumentViews();
			},
			(err: any) => {
				console.error("Failed to load product metadata", err);
				this.alertsService.error({
					message: "Failed to retrieve metadata for this document."
				});
			}
		);
	}

	private processMetadata(result: any, highlights?: any) {
		let hl: { [key: string]: any } = {};
		if (highlights && highlights["aw:record"]["print"]) {
			hl = highlights["aw:record"]["print"];
		}

		let productHash = result["csw:GetRecordByIdResponse"].iStoreRecord;

		this.recordTitle = hl["title"] ? hl["title"] : productHash.title;

		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: hl[field.name] ? hl[field.name] : productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name],field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		this.record = productInfo;

		this.loadMetadataViewTemplate();
	}

	private loadMetadataViewTemplate(): void {
		this.metadataService.getTemplate().then(
			(response: any) => {
				let formdata = response;
				let len = (formdata && formdata.tabs) ? formdata.tabs.length : 0;
				for (let i = 0; i < len; i++) {
					let tab = formdata.tabs[i];
					tab.canaccess = true; // placeholder for potential future permissions-based access

					for (let k = 0; k < tab.fields.length; k++) {
						let field = tab.fields[k];
						field.name = '';
						field.value = '';
						field.cleanedValue = '';
						field.maxLength = '';
						field.links = [];
						field.isValidCollection = false;

						let found = undefined;
						for (var key in this.record) {
							if (this.record[key].name === field.id) {
								found = key;

								field.name = this.record[found].name;
								field.value = this.record[found].value;
								field.cleanedValue = this.record[found].cleanedValue;
								field.maxLength = this.record[found].maxLength;
								field.links = this.record[found].links;
								field.isValidCollection = this.record[found].isValidCollection;

								break;
							}
						}
					}

					this.dataform.tabs.push(tab);
				}
			},
			(err: any) => {
				this.dataform.iserror = true;
				console.error("Failed to load template data", err);
				this.alertsService.error({
					message: 'Unable to load metadata template.'
				});
			}
		);
	}

	private loadDocumentViews() {
		this.viewerService.getDocumentViewsInfo(this.productId).then(
			(response: any) => {
				this.views = response;
				this.views.loadDateDisplay = dayjs(this.views.loadDate).format(this.dateFormatDisplay);
			},
			(err: any) => {
				console.error("Failed to load product views", err);
			}
		);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	/**
	 * Prints the product metadata.
	 */
	print(): void {
		let payload = {
			format: 'txt',
			allMetadata: true
		};

		this.productService.printProductMetadata([this.productId], payload).then(
			(response: string) => {
				this.printResponse(response);
			},
			(err: any) => {
				console.error("Failed to print metadata", err);
				this.alertsService.error({
					message: "Failed to print the product metadata."
				});
				this.close();
			}
		);
	}

	private printResponse(data: string): void {
		if (!data) return;

		let formattedData = data.replace(/<br>/g, '</div><div>');
		formattedData = `<div>${formattedData}</div>`;

		let windowContent = `<!DOCTYPE html>
		  <html>
		  <head>
		      <title>Print</title>
		      <script>
		          function doPrint() { window.print(); }
		      </script>
		      <style>
		          div { white-space: pre-wrap !important; }
		      </style>
		  </head>
		  <body onload="doPrint()">
		      <div><a href="javascript:window.print();">Print</a></div>
		      <div><pre>${formattedData}</pre></div>
		  </body>
		  </html>`;

		let printWin = window.open('', '_blank');
		if (printWin) {
			printWin.document.open();
			printWin.document.write(windowContent);
			printWin.document.close();
		}
	}

	/**
	 * Executes a search using the field and value.
	 */
	resultFieldSearch(field: string, value: string): void {
		console.log(`Searching for: ${field}, with value: ${value}`);
		let payload = {
			field: field,
			value: value
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
		this.close();
	}
	handleBrowsepathLink(link: string): void {
		let payload = {
			link: link,
			source: 'metadata-view'
		};
		this.searchService.broadcastBrowseLinkEvent(payload);		
		this.close();
	}
	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}